import type {
  AttachmentItem,
  BankAccountCreate,
  BankAccountType,
  ReservationOfRightsStatus,
} from "./api"
import { SignatureServiceMap } from "./api"
import { apiClient } from "./client"
import { ExpiredTokenError } from "./errors"

export type SeveranceDocumentStatus = "in_process" | "signed" | "rejected"

type SignatureService =
  (typeof SignatureServiceMap)[keyof typeof SignatureServiceMap]

export type SeveranceDocument = {
  id: number
  name: string
  service: SignatureService
  status: SeveranceDocumentStatus
  file_url: string
  attachments: AttachmentItem[]
  created_at: string
  updated_at: string
}

export type SeveranceStartResponse = {
  success: boolean
  next_step: string
}

export type SeveranceRightsStatus = ReservationOfRightsStatus

export type SeveranceBankAccountResponse = {
  id: number
  holder_id: string
  holder_name: string
  number: string
  type: BankAccountType
  bank_id: number
  bank: {
    id: number
    name: string
    code: string
    disabled: boolean
  }
  confirmed_at: string | null
}

export type SeveranceBankAccountStatus = {
  bank_account: SeveranceBankAccountResponse | null
}

export type SeveranceCompleteResponse = {
  redirect_url: string
}

export type SeveranceSigneeStatus =
  | "in_process"
  | "signing"
  | "signed"
  | "failed"
  | "rejected"

export type SeveranceSignee = {
  id: number
  email: string
  status: SeveranceSigneeStatus
}

export const getSeveranceDocument = ({
  document_id,
}: {
  document_id: number
}) =>
  apiClient
    .get<SeveranceDocument>(`/sign/severance/documents/${document_id}`)
    .catch((e) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const startSeveranceProcess = () =>
  apiClient.post<SeveranceStartResponse>("/sign/severance/start").catch((e) => {
    if (e.status === 401) throw new ExpiredTokenError()
    throw e
  })

export const getSeveranceRightsStatus = () =>
  apiClient
    .get<SeveranceRightsStatus>("/sign/severance/rights/status")
    .catch((e) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const submitSeveranceRights = (data: {
  reservation_of_rights: boolean
  reservation_of_rights_content: string | null
}) =>
  apiClient
    .post<SeveranceRightsStatus>("/sign/severance/rights", {
      reservation_of_rights: data.reservation_of_rights,
      reservation_of_rights_content: data.reservation_of_rights_content,
    })
    .catch((e) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getSeveranceBankAccount = () =>
  apiClient
    .get<SeveranceBankAccountStatus>("/sign/severance/bank-account")
    .catch((e) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const updateSeveranceBankAccount = ({
  holder_id,
  holder_name,
  number,
  type,
  bank_id,
}: BankAccountCreate) =>
  apiClient
    .put<SeveranceBankAccountStatus>("/sign/severance/bank-account", {
      holder_id,
      holder_name,
      number,
      type,
      bank_id,
    })
    .catch((e) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const confirmSeveranceBankAccount = () =>
  apiClient
    .post<SeveranceBankAccountStatus>("/sign/severance/bank-account/confirm")
    .catch((e) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const completeSeveranceProcess = () =>
  apiClient
    .post<SeveranceCompleteResponse>("/sign/severance/complete")
    .catch((e) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getSeveranceSignee = ({
  document_id,
  signee_id,
}: {
  document_id: number
  signee_id: number
}) =>
  apiClient
    .get<SeveranceSignee>(
      `/sign/severance/documents/${document_id}/signees/${signee_id}`,
    )
    .catch((e) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })
